import { authorizedWS } from "@moonscape/shared";

const LAST_EVENT_ID_KEY = "ws-lastEventId";

interface WebSocketCallbacks {
  onOpen?: () => void;
  onMessage?: (data: any) => void;
  onError?: (error: Event) => void;
  onClose?: (event: CloseEvent) => void;
}

/**
 * Singleton WebSocket service
 * Ensures only one WebSocket connection exists across the entire application.
 * Tracks lastEventId from Redis Streams for reconnect catch-up.
 */
class WebSocketService {
  private static instance: WebSocketService | null = null;
  private socket: WebSocket | null = null;
  private isConnecting: boolean = false;
  private reconnectAttempts: number = 0;
  private shouldReconnect: boolean = true;
  private callbacks: Set<WebSocketCallbacks> = new Set();
  private getToken: (() => Promise<string>) | null = null;
  private lastEventId: string | null = null;

  private constructor() {
    // Restore lastEventId from sessionStorage on init
    try {
      this.lastEventId = sessionStorage.getItem(LAST_EVENT_ID_KEY);
    } catch (_) {}
  }

  static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  /**
   * Initialize the WebSocket service with auth token getter
   */
  initialize(getToken: () => Promise<string>) {
    console.log("🔍 [ws-service] Initializing WebSocket service");
    this.getToken = getToken;
  }

  /**
   * Persist lastEventId to sessionStorage for page-refresh survival.
   */
  private saveLastEventId(eventId: string) {
    this.lastEventId = eventId;
    try {
      sessionStorage.setItem(LAST_EVENT_ID_KEY, eventId);
    } catch (_) {}
  }

  /**
   * Subscribe to WebSocket events
   */
  subscribe(callbacks: WebSocketCallbacks): () => void {
    console.log("🔍 [ws-service] New subscriber added, total subscribers:", this.callbacks.size + 1);
    this.callbacks.add(callbacks);

    // Auto-connect ONLY if not already connected or connecting
    if (!this.socket || this.socket.readyState === WebSocket.CLOSED) {
      if (!this.isConnecting) {
        console.log("🔍 [ws-service] No active connection, initiating connect...");
        this.connect();
      } else {
        console.log("🔍 [ws-service] Already connecting, waiting...");
      }
    } else if (this.socket.readyState === WebSocket.OPEN) {
      console.log("🔍 [ws-service] Already connected, notifying subscriber immediately");
      // Immediately notify this subscriber that we're connected
      callbacks.onOpen?.();
    } else {
      console.log("🔍 [ws-service] Connection in progress (readyState:", this.socket.readyState, "), waiting...");
    }

    // Return unsubscribe function
    return () => {
      console.log("🔍 [ws-service] Subscriber removed, remaining subscribers:", this.callbacks.size - 1);
      this.callbacks.delete(callbacks);

      // If no more subscribers, close the connection
      if (this.callbacks.size === 0) {
        console.log("🔍 [ws-service] No more subscribers, closing connection");
        this.disconnect();
      }
    };
  }

  /**
   * Connect to WebSocket
   */
  async connect(): Promise<void> {
    // Prevent duplicate connections
    if (this.isConnecting) {
      console.log("🔍 [ws-service] Already connecting, skipping...");
      return;
    }

    // If already connected, skip
    if (this.socket?.readyState === WebSocket.OPEN) {
      console.log("🔍 [ws-service] Already connected (readyState: 1), skipping...");
      return;
    }

    if (!this.getToken) {
      console.error("🚨 [ws-service] Token getter not initialized");
      return;
    }

    if (!this.shouldReconnect) {
      console.log("[ws-service] Reconnection disabled, not connecting");
      return;
    }

    this.isConnecting = true;
    console.log("🔍 [ws-service] Connecting...");

    try {
      const token = await this.getToken();
      console.log("🔍 [ws-service] Token received:", token ? "✅ Yes" : "❌ No");

      if (!token) {
        console.error("🚨 [ws-service] No token available - cannot connect");
        this.isConnecting = false;
        return;
      }

      // Close existing connection if any
      if (this.socket) {
        console.log("🔍 [ws-service] Closing existing WebSocket...");
        try {
          this.socket.close(1000, "Reconnecting");
        } catch (e) {
          console.log("🔍 [ws-service] Error closing existing socket:", e);
        }
      }

      console.log("🔍 [ws-service] Creating WebSocket connection...");
      this.socket = authorizedWS(token);
      console.log("🔍 [ws-service] WebSocket created");
      console.log("🔍 [ws-service] WebSocket URL:", this.socket.url);
      console.log("🔍 [ws-service] WebSocket readyState:", this.socket.readyState);

      this.socket.onopen = () => {
        console.log("✅ [ws-service] CONNECTED - readyState:", this.socket?.readyState);
        console.log("✅ [ws-service] Subscribers to notify:", this.callbacks.size);
        this.isConnecting = false;

        // If we have a lastEventId (reconnect or page refresh), request catch-up for missed events
        if (this.lastEventId && this.socket) {
          console.log(`🔄 [ws-service] Sending catch-up from lastEventId: ${this.lastEventId}`);
          this.socket.send(JSON.stringify({
            type: "catch-up",
            lastEventId: this.lastEventId,
          }));
        }

        this.reconnectAttempts = 0;

        // Notify all subscribers
        this.callbacks.forEach(cb => {
          console.log("✅ [ws-service] Notifying subscriber of connection");
          cb.onOpen?.();
        });
      };

      this.socket.onmessage = (event) => {
        const isNotEmpty = (value: any) => {
          return (
            value !== null &&
            value !== undefined &&
            (Array.isArray(value)
              ? value.length > 0
              : Object.keys(value).length > 0)
          );
        };

        if (isNotEmpty(event.data)) {
          const data = JSON.parse(event.data);
          console.log("[ws-service] Received:", data);

          // Track lastEventId from Redis Streams for reconnect catch-up
          if (data.eventId) {
            this.saveLastEventId(data.eventId);
          }

          // Notify all subscribers
          this.callbacks.forEach(cb => cb.onMessage?.(data));
        }
      };

      this.socket.onerror = (event) => {
        console.error("[ws-service] Error:", event);
        this.isConnecting = false;

        // Notify all subscribers
        this.callbacks.forEach(cb => cb.onError?.(event));
      };

      this.socket.onclose = (event) => {
        console.log(`❌ [ws-service] DISCONNECTED with code ${event.code}: ${event.reason || '(no reason)'}`);
        console.log(`❌ [ws-service] wasClean: ${event.wasClean}, readyState:`, this.socket?.readyState);
        console.log(`❌ [ws-service] Subscribers to notify:`, this.callbacks.size);
        this.isConnecting = false;

        // Notify all subscribers
        this.callbacks.forEach(cb => {
          console.log(`❌ [ws-service] Notifying subscriber of disconnect`);
          cb.onClose?.(event);
        });

        // Don't reconnect on authentication/authorization errors or rate limiting
        if (event.code === 1008 || event.code === 1003) {
          console.log("[ws-service] Not reconnecting due to auth/rate limit error");
          this.shouldReconnect = false;
          return;
        }

        // Don't reconnect on normal closure
        if (event.code === 1000) {
          console.log("[ws-service] Normal closure, not reconnecting");
          return;
        }

        // Only reconnect if we have subscribers
        if (this.callbacks.size > 0) {
          // Exponential backoff with max delay of 30 seconds
          this.reconnectAttempts++;
          const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
          console.log(`[ws-service] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
          setTimeout(() => this.connect(), delay);
        }
      };
    } catch (error) {
      console.error("🚨 [ws-service] Connection error:", error);
      this.isConnecting = false;
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    if (this.socket) {
      console.log("🔍 [ws-service] Disconnecting...");
      this.shouldReconnect = false;
      this.socket.close(1000, "Manual disconnect");
      this.socket = null;
    }
  }

  /**
   * Send message through WebSocket
   */
  send(message: any): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ action: "message", data: message }));
    } else {
      console.error("[ws-service] WebSocket not connected, cannot send message");
    }
  }

  /**
   * Get current connection state
   */
  getReadyState(): number | undefined {
    return this.socket?.readyState;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }
}

export const websocketService = WebSocketService.getInstance();
